import React, { useMemo } from 'react';
import inputMap from './FilterList';
import type { IFilterGroup } from '@/pages/intelligence/components/Filter/FilterBase/_config';
import type {
  UseinputPropType,
  InputListObj,
  InputObj,
} from '@intelligence/types/topCharts';
import SourceExplanation from '@/pages/intelligence/components/SummaryCard/components/SourceExplanation';
import useSummarySourceState from '@/pages/intelligence/components/SummaryCard/useSummarySourceState';
import SummaryCard from '@/pages/intelligence/components/SummaryCard';
import CalibrationMetricsSwitcher from '@intelligence/components/CalibrationMetricsSwitcher';
import FilterGroup from '@/pages/intelligence/components/Filter/FilterBase/FilterGroup';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import classNames from 'classnames';
import { Bubble } from 'tea-component';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import {
  INPUTLIST_ROW,
  INPUTLIST_TYPE_ROW as Row,
  INPUTLIST_TYPE_LEFT as LEFT,
  INPUTLIST_TYPE_RIGHT as RIGHT,
} from '@intelligence/utils/constant';
import HookSafeRenderer from './HookSafeRenderer';

export default (props: UseinputPropType) => {
  const {
    defaultValue,
    curValue,
    inputList = [],
    onFilterChange,
    locale,
    getLocalText,
    isCustomLayoutFilter = false,
    setVisible,
    filterSaveType = '',
    filterData,
    exportTableCom,
    firstRowLeading,
    specialParams = {},
    filterGroups = [],
    onSummaryDisplayChange,
  } = props;

  // console.log('C', props);
  const { formatMessage } = useIntlExtend();

  const getRender = (item: any) => {
    const {
      type = '',
      key,
      render: itemRender,
      visible,
      visibleinlist,
      hoverTip,
      ...rest
    } = item || {};
    const inputVal = curValue[key] || null;
    const inputDefaultVal = defaultValue[key] || null;
    const inputType = type || key;
    // @ts-ignore
    const render = itemRender || inputMap[inputType];
    // visible: 0 | 1 | 2;  // 是否展示 0:不显示筛选器，不带参数  1：显示筛选器， 2：不显示筛选器，带参数
    if (!render || visible === 2) {
      return null;
    }
    const renderProps = {
      ...rest,
      ...specialParams,
      key,
      value: inputVal,
      defaultValue: inputDefaultVal,
      locale,
      getLocalText,
      onChange: (key: string, value: any, isClose: boolean) => {
        onFilterChange(key, value, isClose);
      },
      filterSaveType,
      visibleinlist,
      onSummaryDisplayChange,
      filterKey: key,
    };
    const rendered = (
      <HookSafeRenderer
        key={key}
        hoverTip={hoverTip}
        renderProps={renderProps}
        renderFn={render}
      />
    );
    return <React.Fragment key={key}>{rendered}</React.Fragment>;
  };

  const getFormInputlist = (result: any[]) => {
    const resultTemp: any = [];
    for (const item of result) {
      if (item?.type === Row) {
        resultTemp.push(item);
      } else if (item?.type === LEFT || item?.type === RIGHT) {
        resultTemp[resultTemp?.length - 1]?.list?.push(item);
      }
    }
    return resultTemp;
  };

  // 筛选器自定义InputList，当遇到Row时，则新起一列数组，并换行
  const customInputList = useMemo(() => {
    if (!isCustomLayoutFilter) return inputList;
    if (Array.isArray(inputList) && inputList?.length > 0) {
      const inputListCur =
        inputList[0]?.key === filterKeyMap.ROW
          ? inputList
          : [INPUTLIST_ROW, ...inputList];
      const result: any = [];
      for (const item of inputListCur) {
        if (item?.key === filterKeyMap.ROW) {
          result.push({
            list: [],
            type: Row,
          });
        } else if (item?.key === filterKeyMap.LEFT) {
          result.push({
            list: [],
            type: LEFT,
          });
        } else if (item?.key === filterKeyMap.RIGHT) {
          result.push({
            list: [],
            type: RIGHT,
          });
        } else {
          result[result?.length - 1]?.list?.push(item);
        }
      }
      return getFormInputlist(result);
    }
    return [];
  }, [inputList, isCustomLayoutFilter]);

  const { openSource, handleClickSourceTips } = useSummarySourceState();

  const ifSummaryCardRow = (itemList: InputListObj[]) => {
    // console.log('itemListitemList', itemList);
    if (
      itemList?.length === 1 &&
      [filterKeyMap.SUMMARY_CARD, filterKeyMap.DATA_SOURCE_EXPLANATION_BODY].includes(itemList[0]?.key)
    ) {
      return true;
    }
    // 筛选器行，没有筛选器，也不需要margin-bottom
    if (!(itemList?.findIndex((item: any) => item.visible !== 2) > -1)) {
      return true;
    }
    return false;
  };

  const ifWarpRow = (itemList: InputListObj[]) => {
    if (itemList?.findIndex((item: any) => item.type === LEFT) > -1) {
      return true;
    }
    return false;
  };

  const getItemRender = (item: InputObj | InputListObj, rowIndex?: number) => {
    if (item.type && item.type === LEFT) {
      return (
        <div className="dbi-custom-filter-row-warp-left">
          {rowIndex === 0 && firstRowLeading}
          {item?.list?.map((itemSub: InputListObj) => getItemRender(itemSub, rowIndex))}
        </div>
      );
    }
    if (item.type && item.type === RIGHT) {
      return (
        <div className="dbi-custom-filter-row-warp-right">
          {item?.list?.map((itemSub: InputListObj) => getItemRender(itemSub, rowIndex))}
        </div>
      );
    }
    const keyValue = item?.key || '';
    const visibleValue = item?.visible ?? true;
    switch (keyValue) {
      case filterKeyMap.SUMMARY_CARD:
        return (
          openSource && (
            <SummaryCard {...item} isSourceTips={true} openSource={openSource} />
          )
        );
      // 数据源解释说明内容展开/折叠按钮
      case filterKeyMap.DATA_SOURCE_EXPLANATION_TRIGGER:
        return (
          <div
            ref={specialParams?.dataSourceExplanationTriggerRef}
            className={classNames('dbi_pcconsole_source_explanation-div', item?.className)}
          />
        );
      // 数据源解释说明内容容器
      case filterKeyMap.DATA_SOURCE_EXPLANATION_BODY:
        return (
          <div
            ref={specialParams?.dataSourceExplanationBodyRef}
            className="dbi-datasource-explanation-body-container dbi-benchmark-summary"
          />
        );
      case filterKeyMap.OPINION_EVENT_SETTING:
        return specialParams?.opinionEventSetting ?? null;
      case filterKeyMap.SOURCE_EXPLANATION:
        return (
          <div className={classNames('dbi_pcconsole_source_explanation-div', item?.className)}>
            <SourceExplanation
              textValue={'dbi_pcconsole_source_explanation'}
              handleClickSourceState={handleClickSourceTips}
              openSource={openSource}
              isNeedLeft={false}
            />
          </div>
        );
      case filterKeyMap.SAVE_FILTER_SETTINGS:
        return (
          <Bubble
            dark
            content={
              filterData?.length >= 10
                ? formatMessage({ id: 'dbi_filterSave_my_filter_top10' })
                : ''
            }
          >
            <div
              className={classNames('dbi-ilterSave-setting-custom', {
                'dbi-ilterSave-setting-custom-disabled':
                  filterData?.length >= 10,
              })}
              onClick={() => {
                // setFilterSaveType('usersave-combine-detail-kpitrends');
                if (filterData?.length < 10) {
                  setVisible(true);
                }
              }}
            >
              {formatMessage({ id: 'dbi_filterSave_filter_setting' })}
            </div>
          </Bubble>
        );
      case filterKeyMap.KPITRENDS_REASE:
        return (
          <div
            className="dbi-ilterSave-setting-custom"
            onClick={() => {
              onFilterChange(
                filterKeyMap.KPITRENDS_REASE,
                filterKeyMap.KPITRENDS_REASE,
              );
            }}
          >
            {formatMessage({ id: 'dbi_reset' })}
          </div>
        );
      case filterKeyMap.KPITRENDS_CALIBRATION_METRICS_SWITCHER: {
        if (!visibleValue) return null;
        const { style: calibrationStyle, ...restCalibrationSwitcherProps } = item || {};
        return (
          <CalibrationMetricsSwitcher
            {...restCalibrationSwitcherProps}
            style={{ marginBottom: 10, marginLeft: 'auto', ...calibrationStyle }}
          />
        );
      }
      case filterKeyMap.DOWNLOAD:
        return exportTableCom;
      default:
        return getRender(item);
    }
  };

  const renderGroupedInput = (groups: IFilterGroup[]) => {
    const itemMap = new Map<string, InputListObj>();
    const groupedKeys = new Set<string>();
    groups.forEach((group) => {
      group.filterKeys.forEach((key) => groupedKeys.add(key));
    });

    inputList.forEach((item: InputListObj) => {
      if (item?.key && !itemMap.has(item.key)) {
        itemMap.set(item.key, item);
      }
    });

    const ungroupedItems = inputList.filter((item: InputListObj) => item?.key && !groupedKeys.has(item.key));

    return (
      <>
        {ungroupedItems.length > 0 ? (
          <FilterGroup groupKey="group_head">
            {ungroupedItems.map((item: InputListObj) => getItemRender(item))}
          </FilterGroup>
        ) : null}
        {groups.map((group) => {
          const items = group.filterKeys
            .map((key) => itemMap.get(key))
            .filter(Boolean) as InputListObj[];
          return (
            <FilterGroup
              key={group.key}
              groupKey={group.key}
              titleKey={group.titleKey}
              title={group.title}
              iconCode={group.iconCode}
              className={group.className}
            >
              {items.map((item: InputListObj) => getItemRender(item))}
            </FilterGroup>
          );
        })}
      </>
    );
  };

  const renderInput = () => {
    /** 获取每个筛选器展示 */
    if (isCustomLayoutFilter) {
      return customInputList?.map((item: InputObj, index: number) => (
        <div
          key={index}
          className={classNames('dbi-custom-filter-row', {
            'dbi-custom-filter-row-summarycard': ifSummaryCardRow(item?.list),
            'dbi-custom-filter-row-warp': ifWarpRow(item?.list),
          })}
        >
          {/* {getInputListContent(item)} */}
          {item?.list?.map((itemSub: InputListObj) => getItemRender(itemSub, index))}
        </div>
      ));
    }
    if (filterGroups.length > 0) {
      return renderGroupedInput(filterGroups);
    }
    return inputList?.map((item: InputListObj) => getRender(item));
  };

  return {
    renderInput,
  };
};
